import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Formik } from 'formik'

import { AutoRadio, Form } from 'components/Forms'
import { List } from 'ui'
import { updateSetting } from 'store/actions/settings'
import { Spinner } from 'ui'
import { t } from 'translations'

import styles from './notificationSettingGroup.module.scss'

let defaultValues = {
  notificationLeave: { create: 'none', update: 'none', delete: 'none', smsOnCreate: false },
  notificationVacation: { create: 'none', update: 'none', delete: 'none' },
  notificationAvailabilities: { create: 'none', update: 'none', delete: 'none' },
  notificationVacationChoices: { create: 'none', update: 'none', delete: 'none' },
  notificationProject: { daytodayChanges: 'none' }
}

const NotificationSettingGroup = ({ optionDescriptions, moduleTitle, moduleSetting, moduleOptions, settings, saveNotificationSettings, buttonsPerRow }) => {
  // Used to maintain state displaying loading or saved icon for each radio group
  const [groupState, setGroupState] = useState({})

  const currentSettings = { ...defaultValues[moduleSetting], ...settings[moduleSetting] }

  const DisappearingCheck = () => <i className="ml-2 fas fa-check text-success disappear" />

  // Make sure setting exists before rendering form
  return currentSettings ? (
    <Formik initialValues={currentSettings}>
      {() => (
        <Form noPrompt>
          <h5>{t(`employee.myNotifications.${moduleTitle}_MODULE`)}</h5>
          <List className="mb-2">
            {Object.keys(currentSettings)
              // Filter for only main module settings and not their associated 'sms instead of email' setting, if it exists
              .filter(setting => !setting.match(/^smsOn/))
              // Prevent setting reordering on rerenders
              // Settings should follow order 'create', 'update', 'delete'
              .sort((settingA, settingB) => {
                if (settingA === 'create' || (settingA === 'update' && settingB === 'delete')) return -1
                else if (settingB === 'create' || (settingA === 'delete' && settingB === 'update')) return 1
                else return 0
              })
              .map(setting => {
                // When saving a regular subsetting of the module, need to overwrite the entire module settings with ones that include the new subsetting value
                const onChangeForNotificationCategory = option => saveNotificationSettings(moduleSetting, { ...currentSettings, [setting]: option })

                // When saving the 'sms instead of email' subsetting, if it exists, need to additionally parse the setting name
                const getSmsSettingName = setting => `smsOn${setting[0].toUpperCase() + setting.slice(1)}`
                const onChangeForSms = option => saveNotificationSettings(moduleSetting, { ...currentSettings, [getSmsSettingName(setting)]: option })

                // If passed custom options object, use the specific options for each category
                const options = !Array.isArray(moduleOptions) ? moduleOptions[setting] : moduleOptions

                // If when saving, display spinner, and when done saving, display a green checkmark next to the group
                const isLoading = groupState[moduleSetting + setting] === 'loading'
                const isSaved = groupState[moduleSetting + setting] === 'saved'
                const isLoadingSms = groupState[moduleSetting + getSmsSettingName(setting)] === 'loading'
                const isSavedSms = groupState[moduleSetting + getSmsSettingName(setting)] === 'saved'

                return (
                  <List.Item key={setting}>
                    {/* Display the main notification settings of the module */}
                    <h6 className="mb-1 position-relative">
                      {t(`employee.myNotifications.NOTIFICATION_CATEGORIES.${setting}`)}
                      {isLoading ? <Spinner className="ml-2" /> : isSaved ? <DisappearingCheck /> : null}
                    </h6>

                    {optionDescriptions[setting] ? (
                      <div className={styles['notifications-option-description']}>
                        <small className={styles['notifications-option-description']}>
                          {t(`employee.myNotifications.NOTIFICATION_DESCRIPTIONS.${moduleTitle}_MODULE.${setting}`)}
                        </small>
                      </div>
                    ) : null}
                    <AutoRadio
                      buttonsPerRow={buttonsPerRow}
                      options={options}
                      onChange={onChangeForNotificationCategory}
                      name={setting}
                      translation="employee.myNotifications"
                      moduleName={moduleSetting}
                      groupState={groupState}
                      setGroupState={setGroupState}
                    />
                    {/* If it exists , display subsetting to receive notifications by sms for current module */}
                    {typeof currentSettings[getSmsSettingName(setting)] !== 'undefined' && (
                      <div className="mt-1">
                        <small>
                          {t('employee.myNotifications.NOTIFICATION_METHOD')}{' '}
                          {isLoadingSms ? <Spinner className={`ml-2 ${styles['notifications-icon-sms']}`} /> : isSavedSms ? <DisappearingCheck /> : null}
                        </small>
                        <AutoRadio
                          options={[false, true]}
                          labels={['email', 'sms']}
                          onChange={onChangeForSms}
                          name={getSmsSettingName(setting)}
                          translation="employee.myNotifications"
                          buttonsPerRow={buttonsPerRow}
                          moduleName={moduleSetting}
                          groupState={groupState}
                          setGroupState={setGroupState}
                        />
                      </div>
                    )}
                  </List.Item>
                )
              })}
          </List>
        </Form>
      )}
    </Formik>
  ) : (
    <></>
  )
}

NotificationSettingGroup.propTypes = {
  buttonsPerRow: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  optionDescriptions: PropTypes.objectOf(PropTypes.bool),
  moduleTitle: PropTypes.string.isRequired,
  moduleSetting: PropTypes.string.isRequired,
  moduleOptions: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string))]).isRequired,
  settings: PropTypes.object.isRequired,
  saveNotificationSettings: PropTypes.func.isRequired
}

NotificationSettingGroup.defaultProps = {
  buttonsPerRow: 1,
  optionDescriptions: {}
}

const mapStateToProps = ({ settings }) => ({ settings })

const mapDispatchToProps = dispatch => ({ saveNotificationSettings: (setting, value) => dispatch(updateSetting(setting, value)) })

export default connect(mapStateToProps, mapDispatchToProps)(NotificationSettingGroup)
